/* eslint-disable @typescript-eslint/naming-convention */

// Split out of kHTML.ts 2026-10-19 (owner: smaller units for the reuse reviewer). kHTML.ts
// re-exports both classes, so `import { kRadioGroup } from "./kHTML"` keeps working.
import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "./kStyles";
import { kFormTheme } from "./kFormTheme";
import { kRadioGroupCore } from "../kReusableCoreTS/kHTMLWidgetsCore";

export class kRadioOption {

    // Monotonic counter so every radio gets a DOM-unique, whitespace-free
    // element id. The option *title* can repeat across groups (e.g. "None") and
    // can contain spaces, so it must never be used as the id value: duplicate or
    // space-bearing ids make label clicks toggle the wrong (or no) radio. ID
    // keeps the logical title; the DOM id is synthetic.
    private static _Seq: number = 0;

    public Radio: HTMLInputElement = document.createElement("input");
    public Label: HTMLLabelElement = document.createElement("label");
    public Text: HTMLSpanElement = document.createElement("span");
    public ID: string = "";

    public constructor(ID: string, GroupName: string) {
        kRadioOption._Seq = kRadioOption._Seq + 1;
        const ElementId: string = "kRadioOpt" + kRadioOption._Seq.toString();
        this.Radio.type = "radio";
        this.Radio.id = ElementId;
        this.Radio.name = GroupName;
        // Same shared look and same 24px target as every other checkbox/radio in the estate.
        kFormTheme.Apply(this.Radio, "kCheck");
        this.Text.textContent = ID;
        this.Text.className = "kRadioOptionText";
        this.ID = ID;
        // The radio lives INSIDE its label, so the whole unit is clickable
        // without a for/id match (which the host DOM can break) and the radio +
        // text stay one atomic inline unit.
        this.Label.className = "kRadioOption";
        this.Label.appendChild(this.Radio);
        this.Label.appendChild(this.Text);
        this._PinLayout();
    }

    // Inline layout resets that BEAT any ancestor / host stylesheet — inline
    // styles outrank every non-!important rule. Without these, a container rule
    // like `.kRowFormField label { display:block }` stacks the text under the
    // radio, and a SharePoint `input {}` rule inflates the control. The injected
    // .kRadioGroup stylesheet repeats these as a baseline + theming hook, but
    // this inline copy is the hard guarantee.
    private _PinLayout = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Lbl: CSSStyleDeclaration = this.Label.style;
            Lbl.display = "inline-flex";
            Lbl.alignItems = "center";
            Lbl.gap = "6px";
            Lbl.margin = "0";
            // No inline padding — the pill padding lives in the injected
            // stylesheet (inline padding would override it). The critical
            // anti-stacking props (display/align/gap) stay inline below.
            Lbl.fontWeight = "400";
            Lbl.lineHeight = "1.4";
            Lbl.whiteSpace = "nowrap";
            Lbl.cursor = "pointer";
            const Rad: CSSStyleDeclaration = this.Radio.style;
            // 24, matching .kCheck's hit area (WCAG 2.2 SC 2.5.8). The PAINTED dot is still
            // 16px - kFormTheme draws it with a pseudo-element inside this box - so the control
            // does not look any bigger. These inline copies exist to survive a hostile host
            // stylesheet, so they have to name the same number the sheet does or the two fight.
            Rad.width = "24px";
            Rad.height = "24px";
            Rad.minWidth = "24px";
            Rad.minHeight = "24px";
            Rad.flex = "none";
            Rad.margin = "0";
            Rad.padding = "0";
            Rad.boxSizing = "border-box";
            Rad.cursor = "pointer";
            this.Text.style.fontWeight = "400";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kRadioOption._PinLayout");
        }
    };
}
// Extends the server-safe kRadioGroupCore: the group Name + option labels +
// selected value + Vertical flag + the static disabled-radio HTML (ToHtmlString)
// live in the base. This browser subclass owns the live DOM: a flex container of
// atomic <label> options, each host-CSS-proof (see kRadioOption._PinLayout).
// There is no <table> — flex wraps long option sets and never inherits
// SharePoint's table/td/label styling.
export class kRadioGroup extends kRadioGroupCore {

    private static _StylesInjected: boolean = false;
    private static readonly STYLE_ID: string = "kRadioGroupStyles";

    public Options: kRadioOption[] = [];
    public Div: HTMLDivElement = document.createElement("div");
    public OnChangeCB: ((Value: string) => void) | null = null;

    public constructor(Name: string, Vertical?: boolean) {
        super(Name);
        kRadioGroup._InjectStyles();
        this.Div.className = "kRadioGroup";
        this.Div.setAttribute("role", "radiogroup");
        if (Vertical === true) {
            this.Vertical = true;
            this.Div.classList.add("kRadioGroupVertical");
        }
    }

    // One namespaced stylesheet for every kRadioGroup on the page: the container
    // flex layout + a baseline of the option/input resets + the consumer theming
    // hook (accent-color is intentionally NOT pinned inline, so a consumer can
    // theme it via `.kRadioGroup input[type=radio]`). The high `.kRadioGroup`
    // specificity beats bare-element host rules; the per-option inline styles
    // cover anything even more specific.
    private static _InjectStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kRadioGroup._StylesInjected === true) { return; }
            if (document.getElementById(kRadioGroup.STYLE_ID) !== null) {
                kRadioGroup._StylesInjected = true;
                return;
            }
            // Opt-in strict-CSP path (kStyles.UseAdoptedSheets): a constructed sheet instead of a <style> element.
            if (kStyles.UseAdoptedSheets === true) {
                kStyles.AddStyles(kRadioGroup.STYLE_ID, kRadioGroup.GROUP_CSS);
                kRadioGroup._StylesInjected = true;
                return;
            }
            const Style: HTMLStyleElement = document.createElement("style");
            Style.id = kRadioGroup.STYLE_ID;
            Style.innerHTML = kRadioGroup.GROUP_CSS;
            document.head.appendChild(Style);
            kRadioGroup._StylesInjected = true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kRadioGroup._InjectStyles");
        }
    };

    private static readonly GROUP_CSS: string =
        ".kRadioGroup { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; }\n"
        + ".kRadioGroup.kRadioGroupVertical { flex-direction: column; align-items: flex-start; gap: 8px; }\n"
        // Each option is a rounded pill. Resting + hover colours are CSS custom
        // properties with neutral defaults, so a consumer injects its brand
        // (never hard-code a project colour in shared infrastructure) — e.g. the
        // ISO row form sets --kRadioOptionHover* to its dialog blue.
        + ".kRadioGroup .kRadioOption { display: inline-flex; align-items: center; gap: 6px; margin: 0; padding: 4px 12px; font-weight: 400; line-height: 1.4; white-space: nowrap; cursor: pointer; border: 1px solid var(--kRadioOptionBorder, #d1d1d1); border-radius: 999px; background: var(--kRadioOptionBg, #ffffff); transition: background-color .12s ease, border-color .12s ease, box-shadow .12s ease; }\n"
        + ".kRadioGroup .kRadioOption:hover { background: var(--kRadioOptionHoverBg, #f3f2f1); border-color: var(--kRadioOptionHoverBorder, #8a8886); box-shadow: 0 1px 2px rgba(0,0,0,0.08); }\n"
        // Selected pill: a filled, coloured state (the in-pill radio dot alone is
        // a weak indicator). Colours are themeable; defaults stay neutral.
        + ".kRadioGroup .kRadioOption:has(input:checked) { background: var(--kRadioOptionSelectedBg, #eaeaea); border-color: var(--kRadioOptionSelectedBorder, #8a8886); }\n"
        // Kill the focus SQUARE: the native radio outline + any host box-shadow
        // focus ring (e.g. the form's input:focus rule) are removed from the
        // radio. Keyboard focus instead shows a rounded ring on the pill via
        // :focus-visible — so a mouse-select shows no square, but tab/arrow
        // navigation stays visible.
        + ".kRadioGroup input[type=radio]:focus { outline: none; box-shadow: none; }\n"
        + ".kRadioGroup .kRadioOption:has(input:focus-visible) { box-shadow: 0 0 0 2px var(--kRadioOptionFocusRing, #8a8886); }\n"
        + ".kRadioGroup .kRadioOptionText { font-weight: 400; }\n"
        + ".kRadioGroup input[type=radio] { width: 16px; height: 16px; min-width: 16px; min-height: 16px; margin: 0; padding: 0; flex: none; box-sizing: border-box; cursor: pointer; }\n";

    private _ChoiceChange = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.OnChangeCB !== null) {
                this.OnChangeCB(this.GetSelectedChoice());
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kRadioGroup._ChoiceChange");
        }
    };

    public AddChoice = (NewChoiceTitle: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const NewChoice: kRadioOption = new kRadioOption(NewChoiceTitle, this.Name);
            NewChoice.Radio.addEventListener("change", this._ChoiceChange);
            this.Options.push(NewChoice);
            this.AddChoiceCore(NewChoiceTitle);
            this.Div.appendChild(NewChoice.Label);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kRadioGroup.AddChoice");
        }
    };

    public GetSelectedChoice = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            let CurrOption: kRadioOption | null = null;
            for (CurrOption of this.Options) {
                if (CurrOption.Radio.checked === true) {
                    return CurrOption.ID;
                }
            }
            return "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kRadioGroup.GetSelectedChoice");
            return "";
        }
    };

    public SetSelectedChoice = (Choice: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrOption: kRadioOption | null = null;
            for (CurrOption of this.Options) {
                if (CurrOption.ID === Choice) {
                    CurrOption.Radio.checked = true;
                } else {
                    CurrOption.Radio.checked = false;
                }
            }
            this.SetSelectedCore(Choice);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kRadioGroup.SetSelectedChoice");
        }
    };

    // Stack the options vertically (one per line). Toggles the flex direction on
    // the SAME container — the old implementation appended a SECOND table to Div
    // and left the horizontal one in place, so both rendered at once.
    public SetVertical = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Vertical = true;
            this.Div.classList.add("kRadioGroupVertical");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kRadioGroup.SetVertical");
        }
    };
}
